import { useCallback, useEffect, useState } from "react";
import type RealtimeKitClient from "@cloudflare/realtimekit";
import { Volume2, X } from "lucide-react";
import Swal from "sweetalert2";
import { ensureMobileSpeakerOutput } from "./mobileSpeakerRouting";

type MobileLowAudioBannerProps = {
    meetingClient: RealtimeKitClient | null;
    mobileOS: "ios" | "android" | "other";
    /** Quando true, exibe o banner (qualquer aba da sala no mobile). */
    visible?: boolean;
    /** Observador / só-ouvindo → sessão playback (alto-falante). */
    listenOnly?: boolean;
    /**
     * Incrementar ao voltar do 2º plano / reconectar para reexibir o banner.
     * Sem isso, após dismiss o usuário “não consegue mais ajustar”.
     */
    resetKey?: number | string;
};

const MobileLowAudioBanner: React.FC<MobileLowAudioBannerProps> = ({
    meetingClient,
    mobileOS,
    visible = true,
    listenOnly = false,
    resetKey = 0,
}) => {
    const [dismissed, setDismissed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState<string | null>(null);

    // Volta do segundo plano / soft reconnect: reabre o aviso de áudio.
    useEffect(() => {
        setDismissed(false);
    }, [resetKey, meetingClient?.peerId]);

    const handleBoost = useCallback(async () => {
        if (!meetingClient || busy) {
            return;
        }

        setBusy(true);
        setStatus(null);
        try {
            // routeKick só aqui (gesto do usuário) — nunca no intervalo da sala.
            // O intervalo antigo trocava o microfone e silenciava quem falava.
            const result = await Promise.race([
                ensureMobileSpeakerOutput(meetingClient, {
                    routeKick: mobileOS === "ios" || mobileOS === "android",
                    listenOnly,
                    replayHiddenPlayer: true,
                }),
                new Promise<null>((resolve) => {
                    window.setTimeout(() => resolve(null), 8000);
                }),
            ]);

            if (!result) {
                setStatus("Não consegui trocar o som. Toque de novo.");
                return;
            }

            const strongOk =
                result.speakerGraph ||
                result.androidSpeakerMic ||
                result.defaultDeviceReset ||
                (result.ok &&
                    (result.mirroredTracks > 0 ||
                        result.iosPulse ||
                        result.sinkIdApplied > 0 ||
                        result.played > 0));

            setStatus(result.message);

            await Swal.fire({
                toast: true,
                position: "top",
                icon: strongOk ? "success" : "warning",
                title: result.speakerGraph || result.androidSpeakerMic
                    ? "Som no alto-falante"
                    : strongOk
                      ? "Áudio retomado"
                      : "Ainda com problema de áudio",
                text: result.message,
                showConfirmButton: false,
                timer: strongOk ? 4500 : 6500,
                timerProgressBar: true,
            });

            // Não auto-fecha o banner: após 2º plano/reconnect o usuário precisa
            // poder tocar de novo. Só some no X manual.
        } finally {
            setBusy(false);
        }
    }, [busy, listenOnly, meetingClient, mobileOS]);

    if (
        dismissed ||
        !meetingClient ||
        !visible ||
        (mobileOS !== "ios" && mobileOS !== "android")
    ) {
        return null;
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className="pointer-events-auto absolute bottom-[calc(7.5rem+env(safe-area-inset-bottom,0px))] left-3 right-3 z-[35] mx-auto flex max-w-md items-center gap-2 rounded-xl border border-amber-400/50 bg-slate-900/95 px-3 py-3 text-white shadow-lg backdrop-blur-sm"
        >
            <Volume2 className="h-5 w-5 shrink-0 text-amber-300" aria-hidden />
            <p className="min-w-0 flex-1 text-xs leading-snug text-slate-100">
                {status ?? (
                    <>
                        Sem som fora do fone? O celular às vezes fica no auricular ou num “fone”
                        fantasma. Toque em{" "}
                        <strong className="font-semibold text-white">Ouvir alto</strong>
                        {" "}(manda o som para o alto-falante, sem desligar seu microfone).
                    </>
                )}
            </p>
            <button
                type="button"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                    event.stopPropagation();
                    void handleBoost();
                }}
                disabled={busy}
                className="shrink-0 touch-manipulation rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-slate-900 disabled:opacity-60"
            >
                {busy ? "…" : "Ouvir alto"}
            </button>
            <button
                type="button"
                onClick={() => setDismissed(true)}
                className="shrink-0 rounded p-0.5 text-slate-500 hover:text-slate-300"
                aria-label="Fechar aviso de áudio"
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
};

export default MobileLowAudioBanner;
